import { JSX, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuCalendarDays, LuCheck, LuHash, LuMapPin } from 'react-icons/lu'
import { Box, Flex, Skeleton, Separator, Stack, Text } from '@chakra-ui/react'
import { Location, LocationType } from '@leid/api-locations'
import { GetPriceResponse } from '@leid/api-orders'
import { format } from 'date-fns'
import { FormikTouched } from 'formik'
import { buildDate, isImmediateRental } from 'app/utils/dates'
import { ItemApi } from 'appTypes/items'
import { CartFormValues } from 'appTypes/orders'
import { DATE_FORMATS } from 'utils/constants'
import { getDateFormatLocale } from 'utils/getDateFormatLocale'
import { removeDecimals, round } from 'utils/removeDecimals'
import { RentalSectionBigHeaderIconWrapper, RentalSectionHeaderSpan } from './styles'

type Props = {
  product: ItemApi
  productPrice: GetPriceResponse | null
  values: CartFormValues
  isProlonging: boolean
  isRerent: boolean
  touched: FormikTouched<CartFormValues>
  productLocations: Location[]
  productPriceLoading: boolean
}

const OrderPreSummaryBox = (props: Props): JSX.Element => {
  const { product, productPrice, values, isProlonging, isRerent, touched, productLocations, productPriceLoading } =
    props

  const { t } = useTranslation(['common'])

  const [selectedLocation, setSelectedLocation] = useState<Location | undefined>(undefined)

  useEffect(() => {
    if (values.selectedLocationId) {
      const locations = productLocations.filter((s) => s.id === values.selectedLocationId)
      setSelectedLocation(locations.length > 0 ? locations[0] : undefined)
    } else {
      setSelectedLocation(undefined)
    }
  }, [values.selectedLocationId, productLocations])

  const rentedFrom = useMemo<Date | undefined>(() => {
    if (values.startDate && values.pickupAt) {
      return buildDate(values.startDate, values.pickupAt)
    }
  }, [values.startDate, values.pickupAt])

  const rentedTo = useMemo<Date | undefined>(() => {
    if (values.endDate && values.returnAt) {
      return buildDate(values.endDate, values.returnAt)
    }
  }, [values.endDate, values.returnAt])

  const shouldDisplayRentedTo = useMemo<boolean>(
    () => (isProlonging ? Boolean(touched.returnAt) : true),
    [isProlonging, touched.returnAt],
  )

  const formatDate = (date: Date) =>
    format(date, DATE_FORMATS.noYearDate, {
      locale: getDateFormatLocale(),
    })

  // const includedFeesString = useIncludedFeesString(product, values, isProlonging, isRerent, productPrice)

  const getPrice = (productPrice: GetPriceResponse): number => {
    return round(productPrice.rawPrice + productPrice.insurancePrice, 0)
  }

  const getPriceSection = () => {
    if (productPrice?.price) {
      return (
        <>
          <Box as="div">
            <Text fontSize="sm" marginTop="-2px">
              {productPrice.rebateAmount !== undefined ? t('common:rebatedPrice') : t('price')}
            </Text>
            <Text fontSize="md" fontWeight="bold" marginBottom="10px">
              {isRerent ? (
                t('bestPrice')
              ) : (
                <>
                  {removeDecimals(getPrice(productPrice))} {t('nok')}
                </>
              )}
            </Text>

            {productPrice.handlingFee > 0 && (
              <>
                <Text fontSize="sm" marginTop="-2px">
                  {t('reservationFee')}
                </Text>
                <Text fontSize="md" fontWeight="bold">
                  {removeDecimals(productPrice.handlingFee)} {t('nok')}
                </Text>
              </>
            )}

            {productPrice.rebateAmount !== undefined && (
              <>
                <Text fontSize="sm" marginTop="-2px">
                  {t('discount')}
                </Text>
                <Text fontSize="md" fontWeight="bold" marginBottom="10px">
                  {removeDecimals(productPrice.rebateAmount)} {t('nok')}
                </Text>
              </>
            )}

            {values.includeDeductibles &&
              productPrice &&
              productPrice.deductiblesPrice !== undefined &&
              productPrice.deductiblesPrice !== 0 && (
                <>
                  <Text fontSize="sm" marginTop="-2px">
                    {t('reductionOfDeductible')}
                  </Text>
                  <Text fontSize="md" fontWeight="bold">
                    {removeDecimals(productPrice.deductiblesPrice)} {t('nok')}
                  </Text>
                </>
              )}
          </Box>
        </>
      )
    }

    return (
      <Text fontSize="sm" opacity={0.5}>
        ...
      </Text>
    )
  }

  const isAvailable = () => {
    if (isRerent) {
      return false
    }

    if (
      !values.returnAt ||
      !values.endDate ||
      !values.selectedLocationId ||
      !values.selectedRegionId ||
      !values.startDate ||
      !values.pickupAt
    ) {
      return false
    }

    return true
  }

  const isLeidAffiliatedLocation = () => {
    if (!values.selectedLocationId) {
      return false
    }
    const location = productLocations.find((s) => s.id === values.selectedLocationId)
    if (!location) {
      return false
    }
    if (location.type !== LocationType.LEID && location.type !== LocationType.FRANCHISE) {
      return false
    }
    return true
  }

  const productAvailable = isAvailable()
  const isImmediateOrder = isImmediateRental(buildDate(values.startDate, values.pickupAt))

  return (
    <Box padding="22px 18px" border="1px solid {colors.green.a50}" borderRadius="md">
      {productAvailable && (
        <Flex alignItems="center" marginBottom="20px">
          <RentalSectionBigHeaderIconWrapper>
            <LuCheck size={24} />
          </RentalSectionBigHeaderIconWrapper>
          <Text fontSize="md" fontWeight="bold">
            {t('available')}
          </Text>
        </Flex>
      )}

      <Flex justifyContent="space-between" marginBottom="5px">
        <Box>
          {rentedFrom && (
            <Flex alignItems="center" marginBottom="5px">
              <LuCalendarDays size={16} />
              <Text fontSize="sm" fontWeight="semibold" marginLeft="8px">
                <RentalSectionHeaderSpan>{t('common:pickup')}: </RentalSectionHeaderSpan>
                {formatDate(rentedFrom)}
              </Text>
            </Flex>
          )}
          {shouldDisplayRentedTo && rentedTo && (
            <Flex alignItems="center" marginBottom="5px">
              <LuCalendarDays size={16} />
              <Text fontSize="sm" fontWeight="semibold" marginLeft="8px">
                <Box as="span" color="green.a200">
                  {t('common:return')}:{' '}
                </Box>
                {formatDate(rentedTo)}
              </Text>
            </Flex>
          )}
          {(!rentedFrom || !rentedTo) && (
            <Flex alignItems="center" marginBottom="5px">
              <LuCalendarDays size={16} />
              <Text fontSize="sm" fontWeight="semibold" marginLeft="8px">
                {t('avRental')}: {product.averageRentalTime} {t('days')}
              </Text>
            </Flex>
          )}

          {product.allowsToRentManyAssets && (
            <Flex alignItems="center" marginBottom="5px">
              <LuHash size={16} style={{ marginTop: '3px' }} />
              <Text fontSize="sm" marginLeft="8px" fontWeight="semibold">
                <Box as="span" color="green.a200">
                  {t('common:Amount')}:{' '}
                </Box>
                {values.amount}
              </Text>
            </Flex>
          )}
          {selectedLocation && (
            <Flex marginBottom="5px">
              <LuMapPin size={16} style={{ marginTop: '3px' }} />
              <Box>
                <Text fontSize="sm" fontWeight="semibold" marginLeft="8px">
                  {selectedLocation.name}
                </Text>
                <Text fontSize="sm" opacity={0.5} marginLeft="8px">
                  {selectedLocation.address}
                </Text>
              </Box>
            </Flex>
          )}
        </Box>
        <Box>
          {!productPriceLoading ? (
            <>
              {productPrice && (
                <>
                  <Flex alignItems="center" marginBottom="5px">
                    {getPriceSection()}
                  </Flex>

                  {productPrice?.deposit !== undefined && (
                    <Flex alignItems="left" marginBottom="5px" flexDirection="column" marginRight="70px">
                      <Text as="span" fontWeight="normal" fontSize="sm">
                        {t('common:deposit')}
                      </Text>
                      <Text fontSize="md" fontWeight="bold">
                        {removeDecimals(productPrice.deposit)} {t('nok')}
                      </Text>
                    </Flex>
                  )}
                </>
              )}
            </>
          ) : (
            <Stack flex="1">
              <Skeleton height="16" color="green.a200" width="100px" />
              <Skeleton height="16" width="80%" color="green.a200" />
            </Stack>
          )}
        </Box>
      </Flex>

      {productAvailable && isLeidAffiliatedLocation() && !isImmediateOrder && (
        <Text fontSize="sm" marginTop="15px" opacity={0.8}>
          {t('canBeCanceled')}
        </Text>
      )}

      <Separator marginTop="30px" />

      {/* TOTAL PRICE */}
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontSize="sm" marginTop="15px" opacity={0.8}>
          {t('totalPrice')}
        </Text>
        {!productPriceLoading ? (
          <Text fontSize="md" marginTop="15px" fontWeight="bold" marginRight="70px" textAlign="left">
            {removeDecimals((productPrice?.price ?? 0) + (productPrice?.deposit ?? 0))} {t('nok')}
          </Text>
        ) : (
          <>
            <Skeleton height="16" color="green.a200" width="100px" />
          </>
        )}
      </Flex>
    </Box>
  )
}

export default OrderPreSummaryBox
